import { Drawer } from '@material-ui/core'
import { Button } from 'components/facets'
import { useTransactionsPolling } from 'lib/hooks/useTransactionsPolling'
import { useTransactionsSpinnerStore } from 'lib/hooks/useTransactionsSpinnerStore'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { ActionButton } from '../ActionButton'
import gear from './gear.gif'
import './spinner.css'

const AWAITING_SNACKBAR_KEY = 'cardtrader-awaiting-review'

export const SpinnerDrawer = (): JSX.Element => {
  const { t } = useTranslation()
  const history = useHistory()
  const { pathname } = useLocation()
  const {
    showSpinnerLoading,
    openSpinnerDrawer,
    setOpenSpinnerDrawer,
    setNumberOfFailedTransactions,
  } = useTransactionsSpinnerStore()

  const { fetchUnprocessedTransactions, transactions, syncs, awaitingUser } =
    useTransactionsPolling()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const isFirstTime = useRef(localStorage.getItem('openSpinnerDrawer') === null)
  const awaitingSnackbarShown = useRef(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [lastFailedTransaction, setLastFailedTransaction] = useState(0)

  useEffect(() => {
    fetchUnprocessedTransactions() // first fetch

    if (showSpinnerLoading) {
      intervalRef.current = setInterval(fetchUnprocessedTransactions, 4000)
      // Showing the drawer the first time is how the user finds it, but not over
      // something already waiting on them: the snackbar is saying the same thing
      // and asking for a decision, and the drawer lands on top of it.
      if (isFirstTime.current && awaitingUser.length === 0) setOpenSpinnerDrawer(true)
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [showSpinnerLoading, awaitingUser.length])

  // The drawer opens by itself only the first time ever, and the spinner icon
  // alone is too quiet for something waiting on a decision: hence the snackbar.
  useEffect(() => {
    // On the account page the review is one click away, and the snackbar would
    // be telling the user to go where they already are — including the one they
    // arrived with.
    if (pathname.startsWith('/account')) {
      awaitingSnackbarShown.current = false
      closeSnackbar(AWAITING_SNACKBAR_KEY)

      return
    }

    if (awaitingUser.length > 0 && !awaitingSnackbarShown.current) {
      awaitingSnackbarShown.current = true

      enqueueSnackbar(t('spinner.awaitingUserMessage', { channel: awaitingUser[0].type }), {
        key: AWAITING_SNACKBAR_KEY,
        persist: true,
        variant: 'info',
        action: (key) => (
          <Button
            variant="contained"
            color="primary"
            data-testid="spinner-go-to-sales-channels"
            onClick={() => {
              closeSnackbar(key)
              history.push('/account?tab=salesChannels')
            }}
          >
            {t('spinner.goToSalesChannels')}
          </Button>
        ),
      })
    }

    if (awaitingUser.length === 0 && awaitingSnackbarShown.current) {
      awaitingSnackbarShown.current = false
      closeSnackbar(AWAITING_SNACKBAR_KEY)
    }
  }, [awaitingUser, pathname])

  useEffect(() => {
    return () => closeSnackbar(AWAITING_SNACKBAR_KEY)
  }, [])

  useEffect(() => {
    if (lastFailedTransaction > 0) {
      setNumberOfFailedTransactions(lastFailedTransaction)
      setLastFailedTransaction(0)
    }
  }, [lastFailedTransaction])

  const handleCloseDrawer = () => {
    if (isFirstTime.current) {
      localStorage.setItem('openSpinnerDrawer', 'false')
      isFirstTime.current = false
    }
    setOpenSpinnerDrawer(false)
  }

  return (
    <Drawer anchor="right" open={openSpinnerDrawer} onClose={handleCloseDrawer}>
      <div
        className="p-3 d-flex flex-row align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={handleCloseDrawer}
      >
        <i className="fa-solid fa-caret-right spinner-icon me-2"></i> <span>Close</span>
      </div>
      <div className="transactions-spinner-drawer">
        {showSpinnerLoading && openSpinnerDrawer ? (
          <>
            <div className="text-center w-100">
              <img src={gear} alt="Loading" className="loading-indicator my-3" />
            </div>
            {transactions.length > 0 && (
              <>
                <h4 className="fw-bold">{t('spinner.transactions')}</h4>
                <div className="w-100 d-flex flex-column">
                  {transactions.map((trans, index) => (
                    <div key={index} className="mt-2">
                      <h5 className="m-o p-0 fw-bold">{trans.type}</h5>
                      <span>
                        {t('spinner.remaining')} {t('spinner.changes')}: {trans.quantityUnprocessed}
                      </span>
                      <br />
                      <span>
                        {t('spinner.failed')} {t('spinner.changes')}: {trans.quantityFailed}
                      </span>
                      <p className="pt-2">{t('spinner.syncingMessage', { channel: trans.type })}</p>
                    </div>
                  ))}
                </div>
                <div className="d-flex flex-column align-items-center">
                  <p>{t('spinner.transactionsDetails')}</p>
                  <ActionButton
                    icon="fa-light fa-arrows-repeat"
                    text={t('navigation.inventoryTransactions')}
                    color="variantSecondary"
                    onClick={() => history.push('/inventoryTransactions')}
                  />
                </div>
              </>
            )}
            {awaitingUser.length > 0 && (
              <>
                <h4 className="mt-3">{t('spinner.awaitingUser')}</h4>
                <div className="w-100 d-flex flex-column">
                  {awaitingUser.map((item, index) => (
                    <p key={index} className="mt-2">
                      {t('spinner.awaitingUserMessage', { channel: item.type })}
                    </p>
                  ))}
                </div>
                <div className="d-flex flex-column align-items-center">
                  <ActionButton
                    icon="fa-light fa-arrow-right"
                    text={t('spinner.goToSalesChannels')}
                    color="variantSecondary"
                    onClick={() => {
                      handleCloseDrawer()
                      history.push('/account?tab=salesChannels')
                    }}
                  />
                </div>
              </>
            )}
            {syncs.length > 0 && (
              <>
                <h4 className="mt-3">{t('spinner.syncs')}</h4>
                <div className="w-100 d-flex flex-column">
                  {syncs.map((sync, index) => (
                    <div key={index}>
                      <h6 className="m-o p-0 mt-2">{sync.type}</h6>
                      {sync.ongoingSyncs.map((sourceInstance, sourceInstanceIndex) => (
                        <p key={sourceInstanceIndex}>{sourceInstance}</p>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div>
            <p>{t('spinner.transactionsCompleted')}</p>
          </div>
        )}
      </div>
    </Drawer>
  )
}
